/**
 * THE SECURITY BOUNDARY.
 *
 * Everything a stranger wrote is rendered in here and nowhere else. Four
 * properties do the work, and the first two are the ones that actually matter:
 *
 *  1. `sandbox` WITH NEITHER `allow-scripts` NOR `allow-same-origin`. The frame
 *     gets a unique opaque origin: no script runs, and even if one did it could
 *     not reach this application's storage, cookies, service worker or DOM.
 *     Granting both together would be worse than granting neither — a document
 *     that is same-origin AND can run script can simply remove its own sandbox
 *     attribute from the parent. They are never both present here, and there is
 *     a test that says so.
 *  2. `srcdoc` WITH ITS OWN CSP, `default-src 'none'`. Belt to the sandbox's
 *     braces: nothing loads, from anywhere, unless a directive below names it.
 *     The only directive that names anything is `img-src data:`, and that is
 *     not a compromise — it is the fix. THIS FRAME FETCHES NOTHING FROM
 *     ANYWHERE, INCLUDING FROM US. Every image it shows is bytes the pane
 *     already had, written into the document as `data:` before it was handed
 *     over. See `documentFor`.
 *  3. No `allow-popups`, no `allow-forms`, no `allow-top-navigation`. A link
 *     opens because the anchor carries `target="_blank"` and the sandbox permits
 *     exactly that one escape via `allow-popups-to-escape-sandbox`, which hands
 *     the new tab a NORMAL origin rather than an opaque one — otherwise every
 *     link a reader follows lands in a broken page.
 *  4. IT FILLS ITS CONTAINER AND SCROLLS ITSELF. The first version tried to size
 *     the frame to its content by reading `contentDocument.body.scrollHeight`
 *     from the parent — which CANNOT WORK and never did: the opaque origin that
 *     property 1 buys means the parent cannot read anything inside, so every
 *     measurement threw and the frame sat at its 320px default with the message
 *     clipped and dead space beneath it. The comment even said the access might
 *     be refused; it is refused always, by construction, and shipping it was
 *     wrong.
 *
 *     The alternatives are worse: a script inside the frame to `postMessage` its
 *     height is exactly the one thing the frame must not have, and
 *     `allow-same-origin` would hand it this application's storage. So the
 *     frame takes the height it is given and scrolls internally, which is what
 *     a reading pane wants anyway — the header stays put while the message
 *     moves under it.
 */
import { useRef } from 'react'

export interface HtmlFrameProps {
  /**
   * Already filtered by `sanitiseHtml`, with every image it kept written in as
   * `data:` bytes. This component does not trust it anyway.
   */
  readonly html: string
  readonly title: string
}

/**
 * The document the frame actually holds.
 *
 * The CSP is FIRST in `<head>` because a `<meta http-equiv>` policy applies only
 * to content after it — a stylesheet or an image above it would be outside the
 * policy. There is no `<base>`, so a relative URL in here resolves against this
 * application's own base, which is the correct outcome for mail that assumed a
 * server it is not being served from: `default-src 'none'` then refuses it.
 *
 * `img-src data:` AND NOTHING ELSE, AND THERE IS NO PROP TO WIDEN IT. There was
 * one, and it took the constant `'self'` — which in a document with an OPAQUE
 * ORIGIN (property 1, the sandbox) matches no URL that exists. So "Show images"
 * flipped a flag, the filter kept the sender's URL, and this policy refused
 * every one of them: a button that could not work, in a shape no test could
 * see, because jsdom does not enforce a meta CSP.
 *
 * WIDENING IT TO THIS APPLICATION'S REAL ORIGIN WOULD NOT HAVE FIXED IT EITHER,
 * which is the part worth writing down. A request this document makes is
 * cross-site by construction — an opaque origin has no site for cookies — so
 * the Access session that guards `/_hx/…` is not attached to it, and any
 * browser with third-party cookies off (Safari's default) settles the question
 * before SameSite gets a vote. The frame would ask, Access would answer with a
 * redirect to a login page, and the image would be blank. Bytes are the only
 * thing that means the same inside this frame as outside it.
 */
function documentFor(html: string): string {
  return `<!doctype html>
<html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; form-action 'none'; base-uri 'none'; frame-ancestors 'none';">
<style>
  :root { color-scheme: light dark; }
  html, body { margin: 0; padding: 0; }
  /* The frame fills the pane and scrolls itself, so its own document needs the
     padding a reading pane would otherwise have provided. */
  body { padding: 1rem 1.25rem 3rem; }
  body {
    font: 16px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    color: #1a1a1a;
    background: transparent;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  @media (prefers-color-scheme: dark) { body { color: #e8e8e8; } }
  /* Mail is full of fixed pixel widths that assume a desktop client. Nothing
     may force a horizontal scrollbar on the reading pane. */
  img, table, pre { max-width: 100% !important; }
  img { height: auto; }
  table { table-layout: auto !important; }
  a { color: #2b6cb0; }
  @media (prefers-color-scheme: dark) { a { color: #7ab8f5; } }
  blockquote {
    margin: 0 0 0 .75rem; padding-left: .75rem;
    border-left: 2px solid currentColor; opacity: .75;
  }
  /* ALL THAT IS LEFT OF AN IMAGE THAT IS NOT BEING SHOWN — blocked, or fetched
     and refused — is the sender's own description, which the sanitiser puts
     here. Marked out so the reader can tell a caption from a sentence.
     NO BACKTICKS IN THIS BLOCK: it is inside a template literal, and one ends
     the document mid-stylesheet. */
  .hearth-image-alt {
    display: inline-block; padding: 0 .25rem;
    border: 1px dashed currentColor; border-radius: 3px;
    font-size: .875em; opacity: .6;
  }
</style>
</head><body>${html}</body></html>`
}

export function HtmlFrame({ html, title }: HtmlFrameProps): React.JSX.Element {
  const ref = useRef<HTMLIFrameElement>(null)

  return (
    <iframe
      ref={ref}
      className="html-frame"
      // `aria-label` AND NOT `title`. An iframe needs an accessible name, and
      // `title` is the conventional way to give one — but the browser also
      // renders it as a TOOLTIP, which parks a grey box over the message the
      // moment the reader's pointer crosses the pane. `aria-label` names it for
      // assistive technology and draws nothing.
      aria-label={title}
      // NEVER `allow-scripts` AND `allow-same-origin` TOGETHER. Together they
      // are equivalent to no sandbox at all, because the framed document can
      // reach into the parent and remove the attribute. Here neither is present.
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      srcDoc={documentFor(html)}
    />
  )
}
